<template>
  <div>
    <div v-if="!loading">
      <!-- Year selector -->
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <button
          v-for="(year, index) in availableYears"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedYear === year
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectYear(year)"
        >
          {{ year }}
        </button>
      </div>

      <!-- Month selector -->
      <div class="flex flex-wrap items-center gap-2 mb-6">
        <button
          v-for="(month, index) in availableMonths"
          :key="index"
          :class="[
            'px-3 py-1 rounded-full text-sm font-medium transition-colors',
            selectedMonth === month.number
              ? 'bg-primary-500 text-white shadow'
              : 'bg-white text-content-300 shadow hover:text-primary-500',
          ]"
          @click="selectMonth(month)"
        >
          {{ month.label }}
        </button>
      </div>

      <!-- Empty state when no month selected -->
      <div v-if="!report && selectedMonth === null" class="card text-center py-10">
        <p class="text-content-300">Selecciona un mes para ver el reporte.</p>
      </div>

      <div v-if="report">
        <!-- General stats grid -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Salud de Pagos — General</h2>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Total de intentos</p>
            <p class="text-2xl font-bold text-gray-800">{{ report.total_attempts }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Pagos exitosos</p>
            <p class="text-2xl font-bold text-success-500">{{ report.succeeded_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Pagos fallidos</p>
            <p class="text-2xl font-bold text-error-500">{{ report.failed_count }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Monto fallido</p>
            <p class="text-2xl font-bold text-error-500">{{ integerToCurrency(report.failed_amount) }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Tasa de fallo</p>
            <p class="text-2xl font-bold" :class="parseFloat(report.failure_rate) > 10 ? 'text-error-500' : 'text-gray-800'">
              {{ parseFloat(report.failure_rate).toFixed(1) }}%
            </p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Tasa de reintento exitoso</p>
            <p class="text-2xl font-bold text-success-500">{{ parseFloat(report.retry_success_rate).toFixed(1) }}%</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4">
            <p class="text-sm text-gray-500 mb-1">Planes en riesgo</p>
            <p class="text-2xl font-bold text-warning-500">{{ report.quotes_at_risk }}</p>
          </div>
          <div class="bg-white rounded-lg shadow p-4 border-l-4 border-error-500">
            <p class="text-sm text-gray-500 mb-1">Ingresos en riesgo</p>
            <p class="text-2xl font-bold text-error-500">{{ integerToCurrency(report.revenue_at_risk) }}</p>
          </div>
        </div>

        <!-- Top errors -->
        <div class="bg-white rounded-lg shadow p-4 mb-8">
          <h3 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Errores más comunes</h3>
          <div v-if="report.top_errors && report.top_errors.length > 0">
            <div
              v-for="error in report.top_errors"
              :key="error.code"
              class="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
            >
              <span class="text-sm text-gray-600">{{ error.message }}</span>
              <span class="ml-4 px-2 py-0.5 bg-red-100 text-error-500 rounded-full text-xs font-semibold">{{ error.count }}</span>
            </div>
          </div>
          <p v-else class="text-sm text-content-300">Sin errores registrados</p>
        </div>

        <!-- At-risk clients table -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Clientes en Riesgo</h2>

        <div v-if="report.at_risk_clients && report.at_risk_clients.length > 0" class="bg-white rounded-lg shadow overflow-hidden mb-8">
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-primary-100">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Contacto</th>
                  <th class="px-4 py-3 text-center text-xs font-semibold text-primary-700 uppercase tracking-wide">Reintentos</th>
                  <th class="px-4 py-3 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Último Error</th>
                  <th class="px-4 py-3 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Valor Semanal</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(client, cIndex) in report.at_risk_clients"
                  :key="client.quote_id"
                  :class="cIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                  class="hover:bg-yellow-50 transition-colors"
                >
                  <td class="px-4 py-2">
                    <router-link
                      :to="{ name: 'quotesDetail', params: { id: client.quote_id } }"
                      class="text-primary-500 hover:underline font-medium"
                    >
                      {{ client.client_name }}
                    </router-link>
                  </td>
                  <td class="px-4 py-2 text-gray-700">{{ client.branch_name || '-' }}</td>
                  <td class="px-4 py-2">
                    <div class="text-gray-700">{{ client.client_email }}</div>
                    <div class="text-xs text-content-300">{{ client.client_phone }}</div>
                  </td>
                  <td class="px-4 py-2 text-center">
                    <span class="inline-block px-2 py-0.5 bg-red-100 text-error-500 rounded-full text-xs font-semibold">{{ client.payment_retry_count }}</span>
                  </td>
                  <td class="px-4 py-2 text-sm text-error-500">{{ client.last_payment_error || '-' }}</td>
                  <td class="px-4 py-2 text-right font-semibold text-gray-800">{{ integerToCurrency(client.total_weekly_price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="bg-white rounded-lg shadow p-6 mb-8 text-center">
          <p class="text-success-500 font-medium">Sin clientes en riesgo este período</p>
        </div>

        <!-- Franchises section -->
        <h2 class="text-lg font-semibold text-gray-700 mb-3">Franquicias</h2>

        <div
          v-if="!report.franchises || report.franchises.length === 0"
          class="card text-center py-6"
        >
          <p class="text-content-300">Sin datos para este período</p>
        </div>

        <div class="flex flex-col gap-4">
          <div
            v-for="franchise in report.franchises"
            :key="franchise.franchise_id"
            class="bg-white rounded-lg shadow overflow-hidden"
          >
            <!-- Franchise header -->
            <div
              class="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
              @click="toggleFranchise(franchise.franchise_id)"
            >
              <div class="flex items-center gap-3">
                <div class="w-2 h-8 rounded-full bg-primary-500"></div>
                <div>
                  <h3 class="font-semibold text-gray-800">{{ franchise.name }}</h3>
                  <p class="text-xs text-gray-500">{{ franchise.branches ? franchise.branches.length : 0 }} sucursal(es)</p>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Intentos</p>
                  <p class="font-semibold text-gray-700">{{ franchise.total_attempts }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Fallidos</p>
                  <p class="font-semibold text-error-500">{{ franchise.failed_count }}</p>
                </div>
                <div class="text-right hidden sm:block">
                  <p class="text-xs text-gray-500">Tasa de fallo</p>
                  <p class="font-semibold" :class="parseFloat(franchise.failure_rate) > 10 ? 'text-error-500' : 'text-gray-700'">
                    {{ parseFloat(franchise.failure_rate).toFixed(1) }}%
                  </p>
                </div>
                <div class="text-right hidden md:block">
                  <p class="text-xs text-gray-500">En riesgo</p>
                  <p class="font-semibold text-warning-500">{{ franchise.quotes_at_risk }}</p>
                </div>
                <svg
                  :class="['w-5 h-5 text-gray-400 transition-transform', isFranchiseExpanded(franchise.franchise_id) ? 'rotate-180' : '']"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <!-- Expanded branch table -->
            <div v-if="isFranchiseExpanded(franchise.franchise_id)" class="border-t border-gray-100">
              <!-- Stats row for mobile -->
              <div class="flex flex-wrap gap-4 px-5 py-3 bg-gray-50 sm:hidden text-sm">
                <div><span class="text-gray-500">Intentos:</span> <span class="font-semibold">{{ franchise.total_attempts }}</span></div>
                <div><span class="text-gray-500">Fallidos:</span> <span class="font-semibold text-error-500">{{ franchise.failed_count }}</span></div>
                <div><span class="text-gray-500">Fallo:</span> <span class="font-semibold">{{ parseFloat(franchise.failure_rate).toFixed(1) }}%</span></div>
                <div><span class="text-gray-500">En riesgo:</span> <span class="font-semibold text-warning-500">{{ franchise.quotes_at_risk }}</span></div>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead class="bg-primary-100">
                    <tr>
                      <th class="px-5 py-2 text-left text-xs font-semibold text-primary-700 uppercase tracking-wide">Sucursal</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Intentos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Fallidos</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Monto fallido</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">Tasa de fallo</th>
                      <th class="px-4 py-2 text-right text-xs font-semibold text-primary-700 uppercase tracking-wide">En riesgo</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(branch, bIndex) in franchise.branches"
                      :key="branch.branch_id"
                      :class="bIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                    >
                      <td class="px-5 py-2 font-medium text-gray-800">{{ branch.branch_name }}</td>
                      <td class="px-4 py-2 text-right text-gray-700">{{ branch.total_attempts }}</td>
                      <td class="px-4 py-2 text-right text-error-500 font-medium">{{ branch.failed_count }}</td>
                      <td class="px-4 py-2 text-right text-error-500">{{ integerToCurrency(branch.failed_amount) }}</td>
                      <td class="px-4 py-2 text-right" :class="parseFloat(branch.failure_rate) > 10 ? 'text-error-500 font-medium' : 'text-gray-700'">
                        {{ parseFloat(branch.failure_rate).toFixed(1) }}%
                      </td>
                      <td class="px-4 py-2 text-right text-warning-500 font-medium">{{ branch.quotes_at_risk }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "PaymentHealth",

  data() {
    return {
      report: null,
      loading: false,
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      expandedFranchises: [],
      months: [
        { number: 1, label: "Enero" },
        { number: 2, label: "Febrero" },
        { number: 3, label: "Marzo" },
        { number: 4, label: "Abril" },
        { number: 5, label: "Mayo" },
        { number: 6, label: "Junio" },
        { number: 7, label: "Julio" },
        { number: 8, label: "Agosto" },
        { number: 9, label: "Septiembre" },
        { number: 10, label: "Octubre" },
        { number: 11, label: "Noviembre" },
        { number: 12, label: "Diciembre" },
      ],
    };
  },

  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const startingYear = 2020;
      const years = [];

      for (let year = startingYear; year <= currentYear; year++) {
        years.push(year);
      }

      return years;
    },

    availableMonths() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (this.selectedYear === currentYear) {
        return this.months.filter((month) => month.number <= currentMonth);
      }

      return this.months;
    },
  },

  created() {
    this.loadReport();
  },

  methods: {
    integerToCurrency,

    toggleFranchise(id) {
      const index = this.expandedFranchises.indexOf(id);
      if (index === -1) {
        this.expandedFranchises.push(id);
      } else {
        this.expandedFranchises.splice(index, 1);
      }
    },

    isFranchiseExpanded(id) {
      return this.expandedFranchises.includes(id);
    },

    async loadReport() {
      this.loading = true;

      try {
        this.report = await api.reports.getPaymentHealthReport([
          { name: "year", value: this.selectedYear },
          {
            name: "month",
            value: this.selectedMonth,
          },
        ]);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    selectYear(year) {
      if (year === this.selectedYear) {
        return;
      }

      this.selectedYear = year;
      this.selectedMonth = null;
      this.report = null;
    },

    selectMonth(month) {
      if (this.selectedMonth === month) {
        return;
      }

      this.selectedMonth = month.number;

      this.loadReport();
    },
  },
};
</script>
